import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { DashboardLayout } from '@/components/DashboardLayout';
import { 
  Search, Plus, Minus, Trash2, ShoppingBag, CreditCard, 
  Banknote, QrCode, Receipt, X, Check, User, Package, Split, Calculator
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const PAYMENT_METHODS = [
  { id: 'dinheiro', name: 'Dinheiro', icon: Banknote, color: 'text-green-400' },
  { id: 'pix', name: 'PIX', icon: QrCode, color: 'text-cyan-400' },
  { id: 'cartao_debito', name: 'Debito', icon: CreditCard, color: 'text-blue-400' },
  { id: 'cartao_credito', name: 'Credito', icon: CreditCard, color: 'text-purple-400' },
];

const POS = () => {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [cart, setCart] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [customerName, setCustomerName] = useState('');
  
  // Payment state
  const [useCombinedPayment, setUseCombinedPayment] = useState(false);
  const [singlePaymentMethod, setSinglePaymentMethod] = useState('dinheiro');
  const [changeFor, setChangeFor] = useState(''); // Valor recebido em dinheiro
  const [combinedPayments, setCombinedPayments] = useState([
    { method: 'dinheiro', amount: 0, changeFor: '' },
    { method: 'pix', amount: 0, changeFor: '' }
  ]);
  
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const searchRef = useRef(null);

  useEffect(() => {
    fetchProducts();
    searchRef.current?.focus();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = products.filter(p => 
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.sku?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredProducts(filtered);
    } else {
      setFilteredProducts([]);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`);
      setProducts(res.data.filter(p => p.is_active));
    } catch (e) {
      toast.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product) => {
    const existing = cart.find(item => item.product_id === product.id);
    
    if (existing) {
      if (product.stock_control && existing.quantity >= product.stock_quantity) {
        toast.error('Estoque insuficiente');
        return;
      }
      setCart(cart.map(item => 
        item.product_id === product.id 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      if (product.stock_control && product.stock_quantity <= 0) {
        toast.error('Produto sem estoque');
        return;
      }
      setCart([...cart, {
        product_id: product.id,
        product_name: product.name,
        quantity: 1,
        unit_price: product.promo_price || product.price,
        stock_quantity: product.stock_quantity,
        stock_control: product.stock_control
      }]);
    }
    
    setSearchTerm('');
    searchRef.current?.focus();
    toast.success(`${product.name} adicionado`);
  };

  const updateQuantity = (productId, delta) => {
    setCart(cart.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return item;
        if (item.stock_control && newQty > item.stock_quantity) {
          toast.error('Estoque insuficiente');
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(cart.filter(item => item.product_id !== productId));
  };

  const clearCart = () => {
    setCart([]);
    setDiscount(0);
    setCustomerName('');
    setChangeFor('');
    setUseCombinedPayment(false);
    setCombinedPayments([
      { method: 'dinheiro', amount: 0, changeFor: '' },
      { method: 'pix', amount: 0, changeFor: '' }
    ]);
    searchRef.current?.focus();
  };

  const subtotal = cart.reduce((sum, item) => sum + (item.quantity * item.unit_price), 0);
  const total = Math.max(0, subtotal - discount);

  // Calculate change for single payment
  const changeAmount = singlePaymentMethod === 'dinheiro' && changeFor 
    ? Math.max(0, parseFloat(changeFor) - total) 
    : 0;

  // Combined payment helpers
  const updateCombinedPayment = (index, field, value) => {
    setCombinedPayments(prev => prev.map((p, i) => 
      i === index ? { ...p, [field]: value } : p
    ));
  };

  const addPaymentMethod = () => {
    const availableMethods = PAYMENT_METHODS.filter(m => 
      !combinedPayments.find(p => p.method === m.id)
    );
    if (availableMethods.length > 0) {
      setCombinedPayments([...combinedPayments, { 
        method: availableMethods[0].id, 
        amount: 0, 
        changeFor: '' 
      }]);
    }
  };

  const removePaymentMethod = (index) => {
    if (combinedPayments.length > 2) {
      setCombinedPayments(combinedPayments.filter((_, i) => i !== index));
    }
  };

  const combinedTotal = combinedPayments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const combinedRemaining = total - combinedTotal;

  // Auto-fill remaining on last payment
  const autoFillRemaining = () => {
    if (combinedRemaining > 0 && combinedPayments.length > 0) {
      const lastIndex = combinedPayments.length - 1;
      updateCombinedPayment(lastIndex, 'amount', combinedPayments[lastIndex].amount + combinedRemaining);
    }
  };

  const handleFinalizeSale = async () => {
    if (cart.length === 0) {
      toast.error('Adicione produtos a venda');
      return;
    }
    
    if (useCombinedPayment) {
      if (Math.abs(combinedRemaining) > 0.01) {
        toast.error(`Soma dos pagamentos diferente do total. Falta: R$ ${combinedRemaining.toFixed(2)}`);
        return;
      }
    }
    
    setShowConfirmModal(true);
  };

  const confirmSale = async () => {
    setProcessing(true);
    try {
      const saleData = {
        items: cart.map(item => ({
          product_id: item.product_id,
          product_name: item.product_name,
          quantity: item.quantity,
          unit_price: item.unit_price
        })),
        discount: discount,
        customer_name: customerName || null
      };

      if (useCombinedPayment) {
        // Pagamento combinado
        saleData.payment_methods = combinedPayments
          .filter(p => p.amount > 0)
          .map(p => ({
            method: p.method,
            amount: parseFloat(p.amount),
            change_for: p.method === 'dinheiro' && p.changeFor ? parseFloat(p.changeFor) : null
          }));
      } else {
        // Pagamento único
        saleData.payment_method = singlePaymentMethod;
        if (singlePaymentMethod === 'dinheiro' && changeFor) {
          saleData.change_for = parseFloat(changeFor);
        }
      }
      
      const res = await axios.post(`${API}/pos/sale`, saleData);
      
      setLastSale(res.data);
      setShowConfirmModal(false);
      setShowSuccessModal(true);
      clearCart();
      fetchProducts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao finalizar venda');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout title="Vendas na Porta">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#ff6b00]"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Vendas na Porta">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[calc(100vh-140px)] overflow-hidden">
        {/* Product Search */}
        <div className="lg:col-span-2 flex flex-col min-h-0">
          {/* Search Bar */}
          <Card className="card-adegga flex-shrink-0">
            <CardContent className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                <Input
                  ref={searchRef}
                  placeholder="Buscar produto por nome ou codigo..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-[#0f0f15] border-white/10 text-white text-lg h-12"
                  data-testid="pos-search-input"
                />
              </div>
              
              {/* Search Results */}
              {filteredProducts.length > 0 && (
                <div className="mt-3 max-h-80 overflow-y-auto space-y-2">
                  {filteredProducts.map(product => (
                    <div
                      key={product.id}
                      onClick={() => addToCart(product)}
                      className="flex items-center justify-between p-3 bg-[#0f0f15] rounded-lg cursor-pointer hover:bg-white/5 transition-colors"
                      data-testid={`pos-product-${product.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded bg-white/5 flex items-center justify-center">
                          <Package className="w-6 h-6 text-white/20" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{product.name}</p>
                          <p className="text-sm text-white/40">
                            {product.sku && <span className="mr-2">{product.sku}</span>}
                            Estoque: {product.stock_quantity}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-[#ff6b00] font-bold text-lg">
                          R$ {(product.promo_price || product.price).toFixed(2)}
                        </p>
                        {product.promo_price && (
                          <p className="text-white/40 line-through text-sm">
                            R$ {product.price.toFixed(2)}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              {searchTerm && filteredProducts.length === 0 && (
                <p className="mt-3 text-white/40 text-center py-4">
                  Nenhum produto encontrado
                </p>
              )}
            </CardContent>
          </Card>

          {/* Cart Items */}
          <Card className="card-adegga flex-1 overflow-hidden mt-3 min-h-0">
            <CardHeader className="pb-2 flex-shrink-0">
              <CardTitle className="text-white flex items-center gap-2">
                <ShoppingBag className="w-5 h-5 text-[#ff6b00]" />
                Itens da Venda
                {cart.length > 0 && (
                  <Badge className="bg-[#ff6b00] ml-2">{cart.length}</Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-3 pt-0 overflow-y-auto" style={{ maxHeight: 'calc(100% - 50px)' }}>
              {cart.length === 0 ? (
                <div className="text-center py-8 text-white/40">
                  <ShoppingBag className="w-10 h-10 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Busque e adicione produtos</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map(item => (
                    <div 
                      key={item.product_id}
                      className="flex items-center justify-between p-3 bg-[#0f0f15] rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-medium truncate">{item.product_name}</p>
                        <p className="text-sm text-white/40">
                          R$ {item.unit_price.toFixed(2)} cada
                        </p>
                      </div>
                      
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateQuantity(item.product_id, -1)}
                            disabled={item.quantity <= 1}
                            className="h-8 w-8 p-0 text-white/60"
                          >
                            <Minus className="w-4 h-4" />
                          </Button>
                          <span className="text-white font-bold w-8 text-center">
                            {item.quantity}
                          </span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => updateQuantity(item.product_id, 1)}
                            className="h-8 w-8 p-0 text-white/60"
                          >
                            <Plus className="w-4 h-4" />
                          </Button>
                        </div>
                        
                        <p className="text-[#ff6b00] font-bold w-24 text-right">
                          R$ {(item.quantity * item.unit_price).toFixed(2)}
                        </p>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeFromCart(item.product_id)}
                          className="h-8 w-8 p-0 text-red-400"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Payment Panel */}
        <div className="flex flex-col min-h-0 overflow-y-auto">
          {/* Totals */}
          <Card className="card-adegga flex-shrink-0">
            <CardContent className="p-3 space-y-3">
              {/* Customer Name */}
              <div>
                <label className="text-xs text-white/60 mb-1 block">Cliente (opcional)</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
                  <Input
                    placeholder="Nome do cliente"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className="pl-10 bg-[#0f0f15] border-white/10 text-white h-9"
                  />
                </div>
              </div>

              {/* Subtotal & Discount */}
              <div className="space-y-1">
                <div className="flex justify-between text-sm text-white/60">
                  <span>Subtotal</span>
                  <span>R$ {subtotal.toFixed(2)}</span>
                </div>
                
                <div className="flex items-center gap-2">
                  <span className="text-sm text-white/60">Desconto</span>
                  <Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={discount || ''}
                    onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                    className="w-24 bg-[#0f0f15] border-white/10 text-white text-right h-8 text-sm"
                    placeholder="0.00"
                  />
                </div>
              </div>

              {/* Total */}
              <div className="pt-2 border-t border-white/10">
                <div className="flex justify-between items-center">
                  <span className="text-lg text-white font-medium">Total</span>
                  <span className="text-2xl text-[#ff6b00] font-bold">
                    R$ {total.toFixed(2)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Payment Methods */}
          <Card className="card-adegga flex-1 min-h-0 mt-3">
            <CardHeader className="pb-1 pt-3 flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-white text-sm">Pagamento</CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-white/40">Combinado</span>
                  <Switch
                    checked={useCombinedPayment}
                    onCheckedChange={setUseCombinedPayment}
                    className="data-[state=checked]:bg-[#ff6b00]"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-3 pt-0 overflow-y-auto" style={{ maxHeight: 'calc(100% - 40px)' }}>
              {!useCombinedPayment ? (
                /* Single Payment */
                <>
                  <div className="grid grid-cols-2 gap-2">
                    {PAYMENT_METHODS.map(method => (
                      <button
                        key={method.id}
                        onClick={() => setSinglePaymentMethod(method.id)}
                        className={`p-2 rounded-lg border transition-all flex flex-col items-center gap-1 ${
                          singlePaymentMethod === method.id
                            ? 'bg-[#ff6b00]/20 border-[#ff6b00] text-white'
                            : 'bg-[#0f0f15] border-white/10 text-white/60 hover:border-white/20'
                        }`}
                      >
                        <method.icon className={`w-5 h-5 ${singlePaymentMethod === method.id ? 'text-[#ff6b00]' : method.color}`} />
                        <span className="text-sm">{method.name}</span>
                      </button>
                    ))}
                  </div>
                  
                  {/* Change Calculator for Cash */}
                  {singlePaymentMethod === 'dinheiro' && (
                    <div className="mt-3 p-2 bg-green-500/10 border border-green-500/20 rounded-lg space-y-2">
                      <div className="flex items-center gap-2 text-green-400">
                        <Calculator className="w-4 h-4" />
                        <span className="text-sm font-medium">Calcular Troco</span>
                      </div>
                      <div>
                        <label className="text-xs text-white/40 block mb-1">Valor Recebido</label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={changeFor}
                          onChange={(e) => setChangeFor(e.target.value)}
                          placeholder={`R$ ${total.toFixed(2)}`}
                          className="bg-[#0f0f15] border-white/10 text-white h-9"
                        />
                      </div>
                      {changeFor && parseFloat(changeFor) >= total && (
                        <div className="flex justify-between items-center pt-2 border-t border-green-500/20">
                          <span className="text-white/60 text-sm">Troco</span>
                          <span className="text-lg font-bold text-green-400">
                            R$ {changeAmount.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {changeFor && parseFloat(changeFor) < total && (
                        <div className="text-red-400 text-xs">
                          Valor insuficiente! Faltam R$ {(total - parseFloat(changeFor)).toFixed(2)}
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                /* Combined Payment */
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-[#ff6b00] mb-2">
                    <Split className="w-4 h-4" />
                    <span className="text-sm font-medium">Pagamento Combinado</span>
                  </div>
                  
                  {combinedPayments.map((payment, index) => {
                    const methodInfo = PAYMENT_METHODS.find(m => m.id === payment.method);
                    return (
                      <div key={index} className="p-3 bg-[#0f0f15] rounded-lg space-y-2">
                        <div className="flex items-center justify-between">
                          <select
                            value={payment.method}
                            onChange={(e) => updateCombinedPayment(index, 'method', e.target.value)}
                            className="bg-[#0a0a0f] border border-white/10 rounded px-2 py-1 text-white text-sm"
                          >
                            {PAYMENT_METHODS.map(m => (
                              <option key={m.id} value={m.id}>{m.name}</option>
                            ))}
                          </select>
                          {combinedPayments.length > 2 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => removePaymentMethod(index)}
                              className="h-6 w-6 p-0 text-red-400"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-white/40 text-sm">R$</span>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={payment.amount || ''}
                            onChange={(e) => updateCombinedPayment(index, 'amount', parseFloat(e.target.value) || 0)}
                            placeholder="0.00"
                            className="bg-[#0a0a0f] border-white/10 text-white"
                          />
                        </div>
                        
                        {/* Change for cash in combined */}
                        {payment.method === 'dinheiro' && payment.amount > 0 && (
                          <div className="pt-2 border-t border-white/5">
                            <label className="text-xs text-white/40 block mb-1">Valor Recebido (troco)</label>
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={payment.changeFor || ''}
                              onChange={(e) => updateCombinedPayment(index, 'changeFor', e.target.value)}
                              placeholder={`R$ ${payment.amount}`}
                              className="bg-[#0a0a0f] border-white/10 text-white text-sm"
                            />
                            {payment.changeFor && parseFloat(payment.changeFor) >= payment.amount && (
                              <div className="flex justify-between mt-1 text-sm">
                                <span className="text-white/40">Troco:</span>
                                <span className="text-green-400 font-medium">
                                  R$ {(parseFloat(payment.changeFor) - payment.amount).toFixed(2)}
                                </span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  
                  {combinedPayments.length < 4 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={addPaymentMethod}
                      className="w-full text-white/40 hover:text-white"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Adicionar forma
                    </Button>
                  )}
                  
                  {/* Combined Summary */}
                  <div className="pt-3 border-t border-white/10 space-y-1">
                    <div className="flex justify-between text-sm">
                      <span className="text-white/40">Total dos pagamentos</span>
                      <span className={combinedTotal === total ? 'text-green-400' : 'text-yellow-400'}>
                        R$ {combinedTotal.toFixed(2)}
                      </span>
                    </div>
                    {combinedRemaining !== 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">
                          {combinedRemaining > 0 ? 'Falta' : 'Excesso'}
                        </span>
                        <span className={combinedRemaining > 0 ? 'text-red-400' : 'text-yellow-400'}>
                          R$ {Math.abs(combinedRemaining).toFixed(2)}
                        </span>
                      </div>
                    )}
                    {combinedRemaining > 0 && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={autoFillRemaining}
                        className="w-full text-[#ff6b00] hover:text-[#ff8533] text-xs"
                      >
                        Preencher restante automaticamente
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="space-y-2 mt-3 flex-shrink-0">
            <Button
              onClick={handleFinalizeSale}
              disabled={cart.length === 0 || (useCombinedPayment && Math.abs(combinedRemaining) > 0.01)}
              className="w-full h-12 text-base bg-gradient-to-r from-[#ff6b00] to-[#ff8533] hover:from-[#ff8533] hover:to-[#ff9955]"
              data-testid="pos-finalize-btn"
            >
              <Receipt className="w-5 h-5 mr-2" />
              Finalizar Venda
            </Button>
            
            {cart.length > 0 && (
              <Button
                onClick={clearCart}
                variant="ghost"
                className="w-full h-9 text-white/60 hover:text-red-400"
              >
                <X className="w-4 h-4 mr-2" />
                Limpar
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Confirm Modal */}
      <Dialog open={showConfirmModal} onOpenChange={setShowConfirmModal}>
        <DialogContent className="bg-[#13131f] border-white/10">
          <DialogHeader>
            <DialogTitle className="text-white">Confirmar Venda</DialogTitle>
            <DialogDescription className="sr-only">Confirmacao da venda</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-[#0f0f15] rounded-lg p-4 space-y-2">
              {cart.map(item => (
                <div key={item.product_id} className="flex justify-between text-white/80">
                  <span>{item.quantity}x {item.product_name}</span>
                  <span>R$ {(item.quantity * item.unit_price).toFixed(2)}</span>
                </div>
              ))}
            </div>
            
            <div className="space-y-1 text-white/60">
              {discount > 0 && (
                <div className="flex justify-between">
                  <span>Desconto</span>
                  <span className="text-red-400">- R$ {discount.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-white/10">
                <span>Total</span>
                <span className="text-[#ff6b00]">R$ {total.toFixed(2)}</span>
              </div>
            </div>
            
            {/* Payment Info */}
            <div className="space-y-2">
              <span className="text-white/60">Pagamento:</span>
              {useCombinedPayment ? (
                <div className="space-y-1">
                  {combinedPayments.filter(p => p.amount > 0).map((p, i) => {
                    const methodInfo = PAYMENT_METHODS.find(m => m.id === p.method);
                    const change = p.method === 'dinheiro' && p.changeFor ? parseFloat(p.changeFor) - p.amount : 0;
                    return (
                      <div key={i} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-[#ff6b00]/20 text-[#ff6b00]">
                            {methodInfo?.name}
                          </Badge>
                        </div>
                        <div className="text-right">
                          <span className="text-white">R$ {p.amount.toFixed(2)}</span>
                          {change > 0 && (
                            <span className="text-green-400 ml-2">(Troco: R$ {change.toFixed(2)})</span>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Badge className="bg-[#ff6b00]/20 text-[#ff6b00]">
                    {PAYMENT_METHODS.find(m => m.id === singlePaymentMethod)?.name}
                  </Badge>
                  {singlePaymentMethod === 'dinheiro' && changeAmount > 0 && (
                    <span className="text-green-400">
                      (Troco: R$ {changeAmount.toFixed(2)})
                    </span>
                  )}
                </div>
              )}
            </div>
            
            {customerName && (
              <div className="text-white/60">
                Cliente: <span className="text-white">{customerName}</span>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowConfirmModal(false)}>
              Cancelar
            </Button>
            <Button 
              className="bg-[#ff6b00] hover:bg-[#ff8533]" 
              onClick={confirmSale}
              disabled={processing}
            >
              {processing ? 'Processando...' : 'Confirmar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Modal */}
      <Dialog open={showSuccessModal} onOpenChange={setShowSuccessModal}>
        <DialogContent className="bg-[#13131f] border-white/10 text-center">
          <DialogHeader>
            <DialogTitle className="sr-only">Venda Finalizada</DialogTitle>
            <DialogDescription className="sr-only">Venda concluida com sucesso</DialogDescription>
          </DialogHeader>
          <div className="py-6">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Venda Finalizada!</h2>
            {lastSale && (
              <div className="space-y-2 text-white/60">
                <p className="text-lg">Venda #{lastSale.sale_number}</p>
                <p className="text-2xl text-[#ff6b00] font-bold">
                  R$ {lastSale.total.toFixed(2)}
                </p>
                {lastSale.change_amount > 0 && (
                  <p className="text-green-400">
                    Troco: R$ {lastSale.change_amount.toFixed(2)}
                  </p>
                )}
                <p className="text-sm">
                  Estoque atualizado - Lancado no financeiro
                </p>
              </div>
            )}
          </div>
          <Button 
            className="w-full bg-[#ff6b00] hover:bg-[#ff8533]" 
            onClick={() => {
              setShowSuccessModal(false);
              searchRef.current?.focus();
            }}
          >
            Nova Venda
          </Button>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default POS;
